import { sessionHeaders } from 'src/api/teslaTrip'

// ----------------------------------------------------------------------
// The console's Google round trip, as the backend holds it.
//
// The same trip Tesla's is (`teslaTrip`), bound to this browser by the same
// HttpOnly cookie, and for the same reason sent to the console's own origin,
// never to `VITE_BACKOFFICE_URL`: in production the API is another site, and
// its cookie would be third-party here and dropped. Google's two steps are
// GraphQL mutations rather than routes of their own, so nothing new is
// reachable without a session except these two.
// ----------------------------------------------------------------------

export type GoogleTripPurpose = 'sign_in' | 'link'

export type GoogleTripOutcome =
  | {
      purpose: 'sign_in'
      token: string
      invitation: 'accepted' | 'unavailable' | null
    }
  /** The account's authenticator code is owed before Google is linked. */
  | { purpose: 'sign_in'; secondFactorTicket: string }
  /**
   * No console account uses it, and none was made: the person is asked first
   * (`newAccountConfirm`). The address is the one Google proved.
   */
  | { purpose: 'sign_in'; newAccountTicket: string; email: string | null }
  | { purpose: 'link'; linkedEmail: string | null }

/** A refusal from the backend, with its code and what it said. */
export class GoogleTripError extends Error {
  constructor(
    readonly code: string,
    message: string,
  ) {
    super(message)
    this.name = 'GoogleTripError'
  }
}

const UNREACHABLE =
  'Google sign in could not start. Check your connection and try again.'

interface GraphQLAnswer<T> {
  data?: T | null
  errors?: { message?: string; extensions?: { code?: string } }[]
}

const post = async <T>(
  query: string,
  variables: object,
  withSession: boolean,
): Promise<T> => {
  let response: Response
  try {
    response = await fetch(`${window.location.origin}/backoffice/graphql`, {
      method: 'POST',
      // The default, stated: the cookie is this origin's and goes only here.
      credentials: 'same-origin',
      headers: {
        'content-type': 'application/json',
        ...(withSession ? await sessionHeaders() : {}),
      },
      body: JSON.stringify({ query, variables }),
    })
  } catch {
    throw new GoogleTripError('UNREACHABLE', UNREACHABLE)
  }

  const answer = (await response.json().catch(() => ({}))) as GraphQLAnswer<T>
  const [error] = answer.errors ?? []
  if (error || !answer.data) {
    throw new GoogleTripError(
      error?.extensions?.code ?? 'UNKNOWN',
      error?.message ?? UNREACHABLE,
    )
  }

  return answer.data
}

const START = `mutation AuthGoogleStart($input: MutationAuthGoogleStartInput!) {
  authGoogleStart(input: $input) { url }
}`

const FINISH = `mutation AuthGoogleFinish($input: MutationAuthGoogleFinishInput!) {
  authGoogleFinish(input: $input) {
    purpose
    token
    invitation
    secondFactorTicket
    newAccountTicket
    newAccountEmail
    linkedEmail
  }
}`

/** Begins a trip and answers with where to send the browser. */
export const googleTripStart = async ({
  purpose,
  invitationToken,
}: {
  purpose: GoogleTripPurpose
  invitationToken?: string | null
}): Promise<string> => {
  // A link acts for the person signed in; a sign-in needs nobody.
  const { authGoogleStart } = await post<{ authGoogleStart: { url: string } }>(
    START,
    { input: { purpose, invitationToken: invitationToken ?? null } },
    purpose === 'link',
  )
  return authGoogleStart.url
}

/** Finishes the trip Google's return belongs to, in this browser. */
export const googleTripFinish = async (
  code: string,
  state: string,
): Promise<GoogleTripOutcome> => {
  const { authGoogleFinish: outcome } = await post<{
    authGoogleFinish: {
      purpose: GoogleTripPurpose
      token: string | null
      invitation: 'accepted' | 'unavailable' | null
      secondFactorTicket: string | null
      newAccountTicket: string | null
      newAccountEmail: string | null
      linkedEmail: string | null
    }
  }>(FINISH, { input: { code, state } }, true)

  if (outcome.purpose === 'link') {
    return { purpose: 'link', linkedEmail: outcome.linkedEmail }
  }
  if (outcome.secondFactorTicket) {
    return {
      purpose: 'sign_in',
      secondFactorTicket: outcome.secondFactorTicket,
    }
  }
  if (outcome.newAccountTicket) {
    return {
      purpose: 'sign_in',
      newAccountTicket: outcome.newAccountTicket,
      email: outcome.newAccountEmail,
    }
  }
  if (!outcome.token) {
    throw new GoogleTripError('UNKNOWN', 'Google sign in failed. Try again.')
  }
  return {
    purpose: 'sign_in',
    token: outcome.token,
    invitation: outcome.invitation,
  }
}
